import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Drawer from '~/components/Drawer';
import { ReactSelectInput } from '~/common/form/Inputs';
import {
    assignNxtFund,
    createNonClientCampaign,
    createNxtFund,
    fetchNonClientCampaignTypes,
} from '~/services/campaign';
import { fetchFinancialCoordinators } from '~/services/fund-requests';
import {
    labelStyles,
    requiredStyles,
} from '~/components/CampaignDetail/drawers/styles';
import { nxtFundsListUrl } from '~/common/nxtUrl';

type FcPerson = { id: number; first_name: string; last_name: string };
type NxtMode = 'none' | 'link' | 'create';

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

const AddNonClientCampaignDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
}) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [fundName, setFundName] = useState('');
    const [fundId, setFundId] = useState('');
    const [campaignTypeCode, setCampaignTypeCode] = useState('');
    const [fcPersonId, setFcPersonId] = useState('');
    const [nxtMode, setNxtMode] = useState<NxtMode>('create');
    const [nxtFundId, setNxtFundId] = useState('');
    const [nxtDescription, setNxtDescription] = useState('');
    const [nxtGoal, setNxtGoal] = useState('');

    const typesQuery = useQuery({
        queryKey: ['non_client_campaign_types'],
        queryFn: fetchNonClientCampaignTypes,
        enabled: isOpen,
        staleTime: 60_000,
    });

    const fcQuery = useQuery({
        queryKey: ['financial_coordinators_dropdown'],
        queryFn: fetchFinancialCoordinators,
        enabled: isOpen,
        staleTime: 60_000,
    });

    useEffect(() => {
        if (!isOpen) {
            setFundName('');
            setFundId('');
            setCampaignTypeCode('');
            setFcPersonId('');
            setNxtMode('create');
            setNxtFundId('');
            setNxtDescription('');
            setNxtGoal('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (nxtMode === 'create' && !nxtDescription) {
            setNxtDescription(fundName.trim());
        }
    }, [nxtMode]);

    const typeOptions =
        typesQuery.data?.map((t) => ({
            label: t.name,
            value: t.code,
        })) ?? [];

    const fcList: FcPerson[] =
        (fcQuery.data as { results?: FcPerson[] } | undefined)?.results ?? [];

    const fcOptions: { label: string; value: string }[] = fcList.map((p) => ({
        label: `${p.first_name} ${p.last_name}`.trim(),
        value: String(p.id),
    }));

    const createMutation = useMutation({
        mutationFn: async () => {
            const created = await createNonClientCampaign({
                name: fundName.trim(),
                cf_patient_ntaf_id: fundId.trim(),
                campaign_type_code: campaignTypeCode,
                financial_coordinator_person_id: fcPersonId
                    ? Number(fcPersonId)
                    : null,
            });

            let nxtError: string | null = null;
            if (nxtMode === 'link' && nxtFundId.trim()) {
                try {
                    await assignNxtFund(created.campaign_id, nxtFundId.trim());
                } catch (e) {
                    nxtError =
                        e instanceof Error
                            ? `couldn't link NXT fund: ${e.message}`
                            : "couldn't link NXT fund";
                }
            } else if (nxtMode === 'create') {
                const overrides: { description?: string; goal?: number } = {};
                if (nxtDescription.trim()) {
                    overrides.description = nxtDescription.trim();
                }
                if (nxtGoal.trim()) {
                    const n = Number(nxtGoal);
                    if (Number.isFinite(n)) overrides.goal = n;
                }
                try {
                    await createNxtFund(created.campaign_id, overrides);
                } catch (e) {
                    nxtError =
                        e instanceof Error
                            ? `couldn't create NXT fund: ${e.message}`
                            : "couldn't create NXT fund";
                }
            }

            return { ...created, nxtError };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['campaign'] });
            setIsOpen(false);
            navigate(`/campaigns/${data.campaign_id}`);
        },
    });

    const handleSubmit = () => {
        if (!fundName.trim() || !fundId.trim() || !campaignTypeCode) {
            toast.error('Fund name, Fund ID, and Fund type are required.');
            return;
        }
        if (nxtMode === 'link' && !nxtFundId.trim()) {
            toast.error('Enter an NXT fund ID, or change the NXT fund option.');
            return;
        }
        toast.promise(createMutation.mutateAsync(), {
            loading: 'Creating…',
            success: (data) =>
                data.nxtError
                    ? `Campaign created (warning: ${data.nxtError})`
                    : 'Campaign created',
            error: (e: unknown) =>
                e instanceof Error ? e.message : 'Could not create campaign',
        });
    };

    const loading =
        typesQuery.isLoading ||
        fcQuery.isLoading ||
        createMutation.isPending;

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen} title='Add Non-Client Campaign' width='w-[min(100%,480px)]'>
            <p className='mb-4 text-[14px]/[20px] text-body'>
                Fund ID must match Raiser&apos;s Edge, MIP, and CF before you
                create the record. It must be unique in this system.
            </p>

            <label className={labelStyles}>
                Fund name <span className={requiredStyles}>*</span>
            </label>
            <input
                className='mt-[4px] w-full rounded-[4px] border-2 border-black p-[10px] text-[16px]/[20px] transition-colors'
                type='text'
                value={fundName}
                onChange={(e) => setFundName(e.target.value)}
                autoComplete='off'
            />

            <label className={labelStyles}>
                Fund type <span className={requiredStyles}>*</span>
            </label>
            <div className='mt-[4px]'>
                {typesQuery.isError ? (
                    <p className='text-sm text-red'>Could not load types</p>
                ) : (
                    <ReactSelectInput
                        options={typeOptions}
                        value={campaignTypeCode}
                        onChange={(v) => setCampaignTypeCode(v)}
                        placeholder='Select type'
                    />
                )}
            </div>

            <label className={labelStyles}>
                Fund ID (NTAF) <span className={requiredStyles}>*</span>
            </label>
            <input
                className='mt-[4px] w-full rounded-[4px] border-2 border-black p-[10px] font-mono uppercase text-[16px]/[20px] transition-colors'
                type='text'
                maxLength={10}
                value={fundId}
                onChange={(e) => setFundId(e.target.value)}
                autoComplete='off'
            />

            <label className={labelStyles}>
                Financial coordinator
            </label>
            <div className='mt-[4px]'>
                {fcQuery.isError ? (
                    <p className='text-sm text-red'>Could not load coordinators</p>
                ) : (
                    <ReactSelectInput
                        options={fcOptions}
                        value={fcPersonId}
                        onChange={(v) => setFcPersonId(v)}
                        placeholder='Optional'
                    />
                )}
            </div>

            <label className={labelStyles}>NXT Fund (optional)</label>
            <div className='mt-[8px] flex flex-col gap-[6px] text-[14px]/[18px]'>
                <label className='flex items-center gap-[8px]'>
                    <input
                        type='radio'
                        name='nxt-mode'
                        value='none'
                        checked={nxtMode === 'none'}
                        onChange={() => setNxtMode('none')}
                    />
                    Don&apos;t create an NXT fund yet
                </label>
                <label className='flex items-center gap-[8px]'>
                    <input
                        type='radio'
                        name='nxt-mode'
                        value='link'
                        checked={nxtMode === 'link'}
                        onChange={() => setNxtMode('link')}
                    />
                    Link existing NXT fund
                </label>
                <label className='flex items-center gap-[8px]'>
                    <input
                        type='radio'
                        name='nxt-mode'
                        value='create'
                        checked={nxtMode === 'create'}
                        onChange={() => setNxtMode('create')}
                    />
                    Create new NXT fund
                </label>
            </div>

            {nxtMode === 'link' && (
                <>
                    <div className='mt-[12px] rounded-lg bg-blue-50 p-3 text-[13px]/[18px] text-blue-900'>
                        Look up funds by description in NXT here:{' '}
                        <a
                            href={nxtFundsListUrl()}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-medium text-primary underline hover:no-underline'
                        >
                            Blackbaud NXT Funds
                        </a>
                        . Copy the fund ID and paste it below.
                    </div>
                    <label className={labelStyles}>
                        NXT Fund ID <span className={requiredStyles}>*</span>
                    </label>
                    <input
                        className='mt-[4px] w-full rounded-[4px] border-2 border-black p-[10px] font-mono text-[16px]/[20px] transition-colors'
                        type='text'
                        value={nxtFundId}
                        onChange={(e) => setNxtFundId(e.target.value)}
                        placeholder='e.g. 14153'
                        autoComplete='off'
                    />
                </>
            )}

            {nxtMode === 'create' && (
                <>
                    <p className='mt-[12px] text-[13px]/[18px] text-body'>
                        A fund will be created in NXT and linked to this campaign.
                        Most fields are derived from the campaign — you can override
                        the description and goal below.
                    </p>
                    <label className={labelStyles}>Description</label>
                    <input
                        className='mt-[4px] w-full rounded-[4px] border-2 border-black p-[10px] text-[16px]/[20px] transition-colors'
                        type='text'
                        value={nxtDescription}
                        onChange={(e) => setNxtDescription(e.target.value)}
                        autoComplete='off'
                    />
                    <p className='mt-[4px] text-[12px]/[16px] text-gray-500'>
                        Campaign ID is appended automatically for uniqueness.
                    </p>
                    <label className={labelStyles}>Goal (USD)</label>
                    <input
                        className='mt-[4px] w-full rounded-[4px] border-2 border-black p-[10px] text-[16px]/[20px] transition-colors'
                        type='number'
                        min='0'
                        step='1'
                        value={nxtGoal}
                        onChange={(e) => setNxtGoal(e.target.value)}
                        placeholder='Optional'
                    />
                </>
            )}

            <div className='mb-[30px] flex gap-[16px]'>
                <button
                    type='button'
                    className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                    onClick={() => setIsOpen(false)}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type='button'
                    className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {createMutation.isPending ? 'Creating…' : 'Create Campaign'}
                </button>
            </div>
        </Drawer>
    );
};

export default AddNonClientCampaignDrawer;
